import { Checkbox, Layout, Textbox } from "@mcleod/components";
import { MobileCommExAppControl } from "./MobileCommExAppControl";

/**
 * Helper class providing common functionality for mobile communication external app control child layouts.
 * Implements shared component focus handling logic to avoid duplication across child classes.
 */
export class MobileCommExAppControlHelper {
    /**
     * Event handler for component focus events.
     * Highlights the corresponding row in the form builder when a field textbox receives focus.
     * @param event - the focus event
     * @param layout - the layout containing the component
     * @param fieldTextboxIds - array of field textbox IDs for validation
     */
    static componentOnFocus(event: any, layout: Layout, fieldTextboxIds: string[]): void {
        if (event.target instanceof Checkbox) {
            MobileCommExAppControlHelper.clearRowHighlight(layout);
            return;
        }

        const textbox = event.target as Textbox;
        MobileCommExAppControlHelper.handleTextboxFocus(textbox, layout, fieldTextboxIds);
    }

    /**
     * Handles focus event for textbox components.
     * @param textbox - the textbox that received focus
     * @param layout - the layout containing the textbox
     * @param fieldTextboxIds - array of field textbox IDs for validation
     */
    private static handleTextboxFocus(textbox: Textbox, layout: Layout, fieldTextboxIds: string[]): void {
        if (!MobileCommExAppControlHelper.isFieldTextbox(textbox, fieldTextboxIds)) {
            MobileCommExAppControlHelper.clearRowHighlight(layout);
            return;
        }

        const sequenceNumber = MobileCommExAppControlHelper.parseSequenceNumber(textbox.text);
        const owner = layout.owner as MobileCommExAppControl;
        owner.highlightRowBySequence(sequenceNumber);
    }

    /**
     * Checks if the given textbox is a field index textbox.
     * @param textbox - the textbox to check
     * @param fieldTextboxIds - array of field textbox IDs for validation
     * @returns true if the textbox is a field index textbox
     */
    private static isFieldTextbox(textbox: Textbox, fieldTextboxIds: string[]): boolean {
        return fieldTextboxIds.includes(textbox.id);
    }

    /**
     * Parses the sequence number from textbox text.
     * @param text - the text to parse
     * @returns the sequence number, or undefined if text is empty or invalid
     */
    private static parseSequenceNumber(text: string): number | undefined {
        if (text === "") {
            return undefined;
        }

        const num = Number(text);
        return isNaN(num) ? undefined : num;
    }

    /**
     * Clears the row highlight in the form builder.
     * @param layout - the layout to clear the highlight from
     */
    private static clearRowHighlight(layout: Layout): void {
        const owner = layout.owner as MobileCommExAppControl;
        owner.highlightRowBySequence(undefined);
    }
}
